import React, { useEffect, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Autocomplete } from '@repo/ui'

import { TagChip } from '../TagChip'
import { FilterField } from './FilterField'

// Location filter: a multi-select autocomplete built on the shared @repo/ui Autocomplete
// (same house component the "add products" flow uses) — search icon, styled dropdown,
// rounded option rows. Type to narrow by country or city; picks accumulate as chips.
// Options are provided by the page (data-derived): { value, label (city), country }.
// Emits the same { field, operator, value } shape as the other filters.

export interface LocationFilterOption {
  value: string
  label: string
  country: string
  state?: string
  /**
   * EXTRA text this option should be findable by, beyond what it displays.
   *
   * The three visible fields are also the searchable ones, which is right while an option IS a
   * city. It stops being right when one option stands for MANY places — the analytics page files
   * every scan under its country, because `scans.geo_country` is the only geo column the
   * analytics queries have a predicate for, so "United States" is the option and Miami, Austin
   * and Oregon are all inside it with nothing to type against.
   *
   * Kept OUT of `renderOption` deliberately: this is a haystack, not a label, and printing it
   * would put a comma-run of every known city on the row. Optional, so every existing call site
   * (whose options are cities) is unaffected.
   */
  searchText?: string
}

interface LocationFilterProps {
  column?: any
  filter?: string[]
  onChange?: (f: { field: string; operator: string; value: string[] }) => void
}

export const LocationFilter = ({ column, filter = [], onChange = () => {} }: LocationFilterProps) => {
  const [selected, setSelected] = useState<string[]>(filter)
  useEffect(() => setSelected(filter), [filter])

  const options: LocationFilterOption[] = column?.options ?? []
  const selectedOptions = options.filter((o) => selected.includes(o.value))

  const emit = (opts: LocationFilterOption[]) => {
    const next = opts.map((o) => o.value)
    setSelected(next)
    onChange({ field: column?.field, operator: 'isAnyOf', value: next })
  }

  return (
    <FilterField label={column?.headerName ?? 'Location'} showClear={selected.length > 0} onClear={() => emit([])}>
      <Autocomplete
        multiple
        disablePortal
        // The "Location" label already identifies this filter, so the leading search
        // glyph is redundant — keep the filter field iconless (matches the Date field).
        hideStartIcon
        options={options}
        value={selectedOptions}
        onChange={(next: LocationFilterOption[]) => emit(next ?? [])}
        // Label feeds the Autocomplete's text search — include city + state + country
        // so typing any of them narrows the list (search by country / state / city).
        // `searchText` appends the places an option STANDS FOR when it is not itself a
        // city (see the field's doc); undefined everywhere else, so nothing changes there.
        getOptionLabel={(o: LocationFilterOption) =>
          [o.label, o.state, o.country, o.searchText].filter(Boolean).join(', ')
        }
        isOptionEqualToValue={(o: LocationFilterOption, v: LocationFilterOption) => o.value === v.value}
        placeholder="Country, state, or city"
        renderOption={(option: LocationFilterOption) => (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            {/* No leading category icon — every row is a location, so a uniform pin
                discriminates nothing; the city name leads, state + country disambiguate. */}
            <Typography variant="body2">{option.label}</Typography>
            <Typography variant="body3" color="text.secondary" sx={{ ml: 'auto' }}>
              {[option.state, option.country].filter(Boolean).join(', ')}
            </Typography>
          </Stack>
        )}
        renderTags={(value: LocationFilterOption[], getTagProps: any) =>
          value.map((option, index) => (
            <TagChip {...getTagProps({ index })} key={option.value} label={option.label} />
          ))
        }
      />
    </FilterField>
  )
}
